import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import {
  Banner,
  Button,
  ConfirmDialog,
  ErrorState,
  PageHead,
  Skeleton,
  SpecList,
  StatusPill,
  TextArea,
  useToast,
} from "@ui/react";
import { ChatIcon, ChevronLeftIcon } from "@ui/react/icons";
import { ApiError } from "../../lib/api/client";
import {
  acceptBooking,
  cancelBooking,
  completeJob,
  declineBooking,
  getBooking,
  getJob,
} from "../../lib/api/contractor";
import {
  canCancelBooking,
  canStartWork,
  parseBookingStatus,
  parseJobBookingMode,
  parseJobStatus,
} from "../../lib/types/enums";
import { startWorkOnJob } from "../jobs/startWork";
import { JobRating } from "../chat/JobRating";
import type { Booking, Job } from "../../lib/types/models";
import { duration, isRealInstant, km, money, zonedDateTime } from "../../lib/format";
import { getDirectBookingSettings } from "../../lib/api/contractor";
import { MarketplaceModeNotice, useBookableMode } from "../booking-settings/ModeGate";
import { useGeolocation } from "../../lib/useGeolocation";
import { errorMessage } from "../../lib/errorMessage";

type Dialog = "decline" | "cancel" | "done" | null;

/** Turns the accept endpoint's specific conflicts into copy that says what to do. */
function acceptErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    // The customer's slot is held only briefly while you decide; once the hold
    // lapses the slot is released and the request can no longer be accepted.
    if (error.code === "HOLD_EXPIRED") {
      return "This request expired before it was accepted — the customer's slot hold has lapsed and the time has been released. Ask them to book again.";
    }
    if (error.code === "CONFLICT") {
      return "This booking is no longer waiting on you. It may already have been declined, cancelled or expired.";
    }
  }
  return errorMessage(error);
}

export function BookingDetailScreen() {
  const bookable = useBookableMode();
  const { bookingId = "" } = useParams();
  const navigate = useNavigate();
  const geo = useGeolocation();
  const toast = useToast();

  const [state, setState] = useState<{ loading: boolean; error: unknown; data: Booking | null }>({
    loading: true,
    error: null,
    data: null,
  });
  const [zone, setZone] = useState("Africa/Johannesburg");
  const [reloadKey, setReloadKey] = useState(0);
  const [dialog, setDialog] = useState<Dialog>(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  // undefined until the first read answers: a completed booking renders the
  // rating off this job, and "Not rated yet" must not flash while it loads.
  const [job, setJob] = useState<Job | null | undefined>(undefined);

  // Cosmetic only, and it never changes mid-session — keep it out of the
  // booking fetch below so a slow or hanging /profile/direct-booking can't
  // hold the record (and with it Accept/Decline/Cancel) behind a skeleton,
  // and so it doesn't get re-read on every accept-failure refetch.
  useEffect(() => {
    if (!bookable) return;

    let ignore = false;
    getDirectBookingSettings()
      .then((s) => {
        if (!ignore) setZone(s.bookingTimezone || "Africa/Johannesburg");
      })
      .catch(() => {
        if (!ignore) setZone("Africa/Johannesburg");
      });

    return () => {
      ignore = true;
    };
  }, [bookable]);

  useEffect(() => {
    if (!bookable || !bookingId) return;

    let ignore = false;
    setState((s) => ({ ...s, loading: true, error: null }));

    getBooking(bookingId, geo.position?.lat, geo.position?.lng)
      .then((booking) => {
        if (!ignore) setState({ loading: false, error: null, data: booking });
      })
      .catch((error: unknown) => {
        if (!ignore) setState({ loading: false, error, data: null });
      });

    return () => {
      ignore = true;
    };
  }, [bookable, bookingId, reloadKey, geo.position]);

  // The work actions below are JOB actions on the mirrored job — the booking
  // record says the money is held, but only the job says whether work has
  // already started. Kept out of the booking fetch above for the same reason
  // the timezone is: a slow or failing /jobs/:id must never hold the booking
  // record, and with it Accept/Decline/Cancel, behind a skeleton.
  const mirroredJobId = state.data?.jobId;
  useEffect(() => {
    if (!mirroredJobId) {
      setJob(null);
      return;
    }

    let ignore = false;
    getJob(mirroredJobId)
      .then((j) => {
        if (!ignore) setJob(j);
      })
      .catch(() => {
        if (!ignore) setJob(null);
      });

    return () => {
      ignore = true;
    };
  }, [mirroredJobId, reloadKey]);

  const refetch = useCallback(() => setReloadKey((key) => key + 1), []);

  // Must be stable: <Modal> re-runs its focus effect whenever onClose changes,
  // which would pull focus out of the reason box on every keystroke.
  const closeDialog = useCallback(() => setDialog(null), []);

  const openDialog = (next: Exclude<Dialog, null>) => {
    setReason("");
    setActionError(null);
    setDialog(next);
  };

  const onAccept = async () => {
    setBusy(true);
    setActionError(null);
    try {
      const updated = await acceptBooking(bookingId);
      setState({ loading: false, error: null, data: updated });
      toast.show("Booking accepted.");
    } catch (error) {
      setActionError(acceptErrorMessage(error));
      // Every accept failure means the server knows something this page does
      // not, so re-read rather than leaving a stale Accept button on screen.
      refetch();
    } finally {
      setBusy(false);
    }
  };

  const onDecline = async () => {
    setBusy(true);
    setActionError(null);
    try {
      // 204 with no body: there is no booking to read back, so leave rather
      // than rendering a half-updated record.
      await declineBooking(bookingId, reason.trim() || undefined);
      setDialog(null);
      toast.show("Request declined.");
      navigate("/bookings", { replace: true });
    } catch (error) {
      setActionError(errorMessage(error));
      setDialog(null);
    } finally {
      setBusy(false);
    }
  };

  const onCancel = async () => {
    setBusy(true);
    setActionError(null);
    try {
      const updated = await cancelBooking(bookingId, reason.trim() || undefined);
      setState({ loading: false, error: null, data: updated });
      setDialog(null);
      toast.show("Booking cancelled.");
    } catch (error) {
      setActionError(errorMessage(error));
      setDialog(null);
      refetch();
    } finally {
      setBusy(false);
    }
  };

  // Start is a job action too, and it goes through the shared helper so the
  // booking's appointment is marked started in the same breath — POST
  // /jobs/:id/start alone would move the job and strand the appointment in
  // Scheduled forever. The server imposes no clock on starting, so a
  // contractor who arrives early may begin.
  const onStart = async () => {
    if (!job) return;
    setBusy(true);
    setActionError(null);
    try {
      await startWorkOnJob(job);
      toast.show("Work started.");
    } catch (error) {
      setActionError(errorMessage(error));
    } finally {
      setBusy(false);
      refetch();
    }
  };

  // "Done" is a job action: a paid booking is mirrored into a Job the moment
  // payment lands, and it is that job the homeowner signs off on. The mirror
  // moves the booking Paid → InProgress server-side, so the re-read — not the
  // job that comes back — is what swaps the button for the waiting banner.
  const onDone = async () => {
    const jobId = state.data?.jobId;
    if (!jobId) return;
    setBusy(true);
    setActionError(null);
    try {
      await completeJob(jobId);
      toast.show("Marked as done. The customer signs off next.");
    } catch (error) {
      setActionError(errorMessage(error));
    } finally {
      setDialog(null);
      setBusy(false);
      refetch();
    }
  };

  if (!bookable) {
    return (
      <MarketplaceModeNotice
        what="Bookings"
        body="Bookings only appear once you're taking direct bookings. Add a service and your availability first, then switch your operating mode from your profile."
      />
    );
  }

  const booking = state.data;
  const status = booking ? parseBookingStatus(booking.status) : null;
  const wasPaid = status === "paid";
  // Messaging closes with the booking. The rating rides on the mirror job —
  // contractor-api's GET /jobs/:id attaches the review once the job is
  // completed — so the job read above is what carries it here.
  const completed = status === "completed";

  // A Paid booking means the money is held, and it stays Paid until Done hands
  // the job over (which mirrors it to InProgress) — so the booking alone is
  // enough to gate Done, exactly as before.
  const paidWithJob = wasPaid && !!booking?.jobId;
  const canMarkDone = paidWithJob;

  // Start needs the JOB as well, for two reasons: a Paid booking reads the same
  // before and after work begins, so only the job's status says whether Start is
  // still on offer; and startWorkOnJob needs the job to find the appointment
  // door. The predicate is the server's own (types.FundsHeld), which a direct
  // booking satisfies while its job still reads "Scheduled" — testing the
  // status alone is what left every paid booking with no way to start.
  const jobStatus = job ? parseJobStatus(job.status) : null;
  const canStart =
    paidWithJob && !!jobStatus && canStartWork(jobStatus, parseJobBookingMode(job?.bookingMode));

  // contractor-api answers TOO_EARLY on Complete until the slot's start time has
  // passed, and enforces it there because a client-only gate is one curl away
  // from capturing before the contractor could have been on site. The button
  // still APPEARS once paid — disabled, saying when it opens — because silently
  // hiding it is what sent a contractor hunting for a way to finish the job.
  const slotOpen =
    !!booking &&
    isRealInstant(booking.scheduledStartAt) &&
    Date.parse(booking.scheduledStartAt) <= Date.now();

  return (
    <div className="stack stack--loose">
      <PageHead
        eyebrow="Bookings"
        title={
          booking
            ? [booking.serviceName, booking.variantLabel].filter(Boolean).join(" — ") || "Booking"
            : "Booking"
        }
        back={
          <Link className="back-link" to="/bookings">
            <ChevronLeftIcon />
            All bookings
          </Link>
        }
      />

      {actionError ? (
        <Banner tone="danger" title="That didn't work">
          {actionError}
        </Banner>
      ) : null}

      {state.loading ? (
        <div role="status">
          <span className="sr-only">Loading this booking</span>
          <Skeleton height="18rem" />
        </div>
      ) : state.error ? (
        <ErrorState message={errorMessage(state.error)} onRetry={refetch} />
      ) : !booking || !status ? (
        <ErrorState message={errorMessage(new Error("We couldn't find that booking."))} onRetry={refetch} />
      ) : (
        <>
          {status === "inProgress" ? (
            <Banner tone="info" title="Waiting for the customer to sign off">
              You marked this booking done. Your payment is released once they sign off.
            </Banner>
          ) : null}

          <div className="card card--dense">
            <div className="stack">
              <div className="cluster cluster--end">
                <StatusPill kind="booking" status={status} />
              </div>

              {/* Bookings carry true RFC 3339 instants, unlike appointments'
                  naive wall clock — so these render through zonedDateTime(), which
                  renders them in the contractor's bookingTimezone with a zone label. */}
              <SpecList
                columns
                items={[
                  { label: "When", value: zonedDateTime(booking.scheduledStartAt, zone) },
                  { label: "Ends", value: zonedDateTime(booking.scheduledEndAt, zone) },
                  {
                    label: "Duration",
                    value: booking.durationMinutes ? duration(booking.durationMinutes) : null,
                    when: Boolean(booking.durationMinutes),
                  },
                  {
                    label: "Total",
                    value: (
                      <span className="amount">
                        {money(booking.totalAmount, booking.totalCurrency)}
                      </span>
                    ),
                  },
                  { label: "Address", value: booking.address, when: Boolean(booking.address) },
                  // Never both rows at once: contractor-api withholds the
                  // address, and only computes distance, before acceptance.
                  {
                    label: "Distance",
                    value: `${km(booking.distance)} away`,
                    when: !booking.address && Boolean(booking.distance),
                  },
                  { label: "Notes", value: booking.notes, when: Boolean(booking.notes) },
                ]}
              />
              {!booking.address && booking.distance && status === "requested" ? (
                <p className="muted">The address is shared once you accept.</p>
              ) : null}
            </div>
          </div>

          {booking.extras && booking.extras.length > 0 ? (
            <div className="card card--dense">
              <div className="stack stack--tight">
                <h2 className="headline-sm">Add-ons</h2>
                {booking.extras.map((extra) => (
                  <div className="cluster cluster--between" key={extra.extraId}>
                    <span>{extra.label}</span>
                    <span className="muted">
                      + {money(extra.priceAmount, booking.totalCurrency)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {(status === "requested" || canCancelBooking(status)) &&
          isRealInstant(booking.cancellationDeadlineAt) ? (
            <Banner tone="info" title="Free cancellation deadline">
              {new Date(booking.cancellationDeadlineAt) > new Date() ? (
                <>
                  {zonedDateTime(booking.cancellationDeadlineAt, zone)}. Cancelling a paid booking after
                  this still refunds the customer in full, and that comes out of your earnings.
                </>
              ) : (
                <>
                  Closed {zonedDateTime(booking.cancellationDeadlineAt, zone)}. Cancelling a paid booking now
                  still refunds the customer in full, and that comes out of your earnings.
                </>
              )}
            </Banner>
          ) : null}

          {completed ? (
            <div className="card card--dense">
              <div className="stack stack--tight">
                <h2 className="headline-sm">Rating</h2>
                {job === undefined ? <Skeleton height="3rem" /> : <JobRating rating={job?.rating} />}
              </div>
            </div>
          ) : null}

          <div className="cluster">
            {booking.conversationId && !completed ? (
              <Link className="btn btn-secondary" to={`/messages/${booking.conversationId}`}>
                <ChatIcon />
                Message the customer
              </Link>
            ) : null}

            {status === "requested" ? (
              <>
                <Button onClick={() => void onAccept()} loading={busy}>
                  Accept booking
                </Button>
                <Button variant="secondary" onClick={() => openDialog("decline")} disabled={busy}>
                  Decline
                </Button>
              </>
            ) : null}

            {canStart ? (
              <Button variant="secondary" onClick={() => void onStart()} loading={busy}>
                Start work
              </Button>
            ) : null}

            {canMarkDone ? (
              <Button onClick={() => openDialog("done")} disabled={busy || !slotOpen}>
                Mark as done
              </Button>
            ) : null}

            {canMarkDone && !slotOpen ? (
              <span className="label-sm muted">
                You can mark this done from {zonedDateTime(booking.scheduledStartAt, zone)}.
              </span>
            ) : null}

            {canCancelBooking(status) ? (
              <Button variant="secondary" onClick={() => openDialog("cancel")} disabled={busy}>
                Cancel booking
              </Button>
            ) : null}
          </div>
        </>
      )}

      {dialog === "done" ? (
        <ConfirmDialog
          title="Mark this booking as done?"
          confirmLabel="Yes, mark it done"
          confirming={busy}
          onConfirm={() => void onDone()}
          onCancel={closeDialog}
          body={
            <p>
              The customer will be asked to review the work and sign off before your payment is
              released. Only do this once the work is genuinely finished.
            </p>
          }
        />
      ) : null}

      {dialog === "decline" ? (
        <ConfirmDialog
          title="Decline this request?"
          confirmLabel="Decline request"
          cancelLabel="Keep request"
          destructive
          confirming={busy}
          onConfirm={() => void onDecline()}
          onCancel={closeDialog}
          body={
            <>
              <p>
                The customer is told straight away and their held slot is released. This can't
                be undone — they'd have to book again.
              </p>
              <TextArea
                label="Reason (optional)"
                hint="Shared with the customer so they know what to do next."
                value={reason}
                onChange={(event) => setReason(event.target.value)}
              />
            </>
          }
        />
      ) : null}

      {dialog === "cancel" ? (
        <ConfirmDialog
          title="Cancel this booking?"
          confirmLabel="Cancel booking"
          cancelLabel="Keep booking"
          destructive
          confirming={busy}
          onConfirm={() => void onCancel()}
          onCancel={closeDialog}
          body={
            <>
              <p>
                {wasPaid
                  ? "The customer has already paid, so cancelling refunds them in full — the whole amount goes back, no matter how close the booking is. You will not be paid for it."
                  : "The customer is told straight away and the slot is released. Nothing has been paid yet, so there is nothing to refund."}
              </p>
              <p>Cancelling repeatedly puts your account at risk of being blocked from new work.</p>
              <TextArea
                label="Reason (optional)"
                hint="Shared with the customer."
                value={reason}
                onChange={(event) => setReason(event.target.value)}
              />
            </>
          }
        />
      ) : null}
    </div>
  );
}
